import { Controller, useFormContext } from "react-hook-form";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ModalCodeEditor from "../Modals/ModalCodeEditor";
import { Maximize2 } from "lucide-react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/themes/prism.css";
import "prismjs/components/prism-python";
import "prism-themes/themes/prism-one-light.css";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { NoxusIconButton } from "../UI/NoxusIconButton";
import { useDisclosure } from "@/utils/useDisclosure";
import { cn } from "@/lib/utils";
import { InternalService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { SpotToast } from "@/components/SpotToast";
import * as Sentry from "@sentry/nextjs";
import {
  CodeGenerateControls,
  CodeGeneratePromptBox,
} from "./CodeGenerateAssist";
import { Button } from "@/components/shadcn/button";
import "../Form/TiptapEditor/styles.css";

type CodegenVariable = { label: string; value: string };

function normalizeVariablesForCodegen(raw: unknown): CodegenVariable[] {
  if (Array.isArray(raw)) {
    return raw.flatMap((item) => {
      if (!item || typeof item !== "object" || !("label" in item)) {
        return [];
      }
      const label = (item as { label: unknown }).label;
      if (typeof label !== "string" || !label) {
        return [];
      }
      const value = (item as { value?: unknown }).value;
      return [{ label, value: typeof value === "string" ? value : "Text" }];
    });
  }
  if (raw && typeof raw === "object") {
    return Object.entries(raw as Record<string, unknown>)
      .filter(([label]) => label.length > 0)
      .map(([label, row]) => {
        // Typed rows (`ConfigBindableTypedDictRows`) carry the declared type;
        // legacy flat rows are a bare value, so fall back to "Text".
        const type =
          row &&
          typeof row === "object" &&
          !Array.isArray(row) &&
          "type" in row &&
          typeof (row as { type: unknown }).type === "string"
            ? (row as { type: string }).type
            : "Text";
        return { label, value: type };
      });
  }
  return [];
}

interface ICodeFieldAI {
  name: string;
  placeholder?: string;
  label: string;
  isDisabled?: boolean;
  inputVariablesFieldName: string;
  outputVariablesFieldName: string;
  filesAsObjectsFieldName?: string;
  helperText?: string;
}

interface ICodeInputAI {
  label?: string;
  value: string | undefined;
  placeholder?: string;
  isDisabled?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
  isExpanded?: boolean;
  name?: string;
  helperText?: string;
  inputVariables: CodegenVariable[];
  outputVariables: CodegenVariable[];
  filesAsObjects?: boolean;
}

export const CodeInputAI = ({
  label,
  value,
  placeholder,
  isDisabled,
  onChange,
  onBlur,
  isExpanded,
  name,
  helperText,
  inputVariables,
  outputVariables,
  filesAsObjects,
}: ICodeInputAI) => {
  const { spotGroup } = useSpotUser();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const containerRef = useRef(null);

  const [isTextBoxVisible, setIsTextBoxVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [previousCode, setPreviousCode] = useState<string | null>(null);

  const lineNumbers = useMemo(
    () =>
      (value || placeholder || "")
        .split("\n")
        .map((_, index) => index + 1)
        .join("\n"),
    [value, placeholder]
  );

  useEffect(() => {
    if (containerRef.current) {
      (containerRef.current as HTMLDivElement).scrollTop = (
        containerRef.current as HTMLDivElement
      ).scrollHeight;
    }
  }, [lineNumbers]);

  const highlightCode = useCallback((code: string) => {
    return Prism.highlight(code, Prism.languages.python, "python");
  }, []);

  const handleGenerateCode = useCallback(() => {
    if (isLoading || isDisabled || !inputValue) return;

    setIsLoading(true);
    setPreviousCode(value || "");

    InternalService.generateCode({
      path: {
        group_id: spotGroup.id,
      },
      body: {
        user_request: inputValue,
        existing_code: value || null,
        input_variables: inputVariables,
        output_variables: outputVariables,
        files_as_objects: filesAsObjects ?? false,
      },
    })
      .then((response) => {
        if (response.data?.code) {
          onChange(response.data.code);
        }
        setIsLoading(false);
        setIsTextBoxVisible(false);
        setInputValue("");
      })
      .catch((error) => {
        console.error("Error generating code:", error);
        SpotToast.error("Error generating code. Please try again.");
        Sentry.captureException(error, {
          extra: {
            user_request: inputValue,
            existing_code: value,
          },
        });
        setIsLoading(false);
        setPreviousCode(null);
      });
  }, [
    isLoading,
    isDisabled,
    inputValue,
    value,
    spotGroup.id,
    inputVariables,
    outputVariables,
    filesAsObjects,
    onChange,
  ]);

  const handleRevertChange = useCallback(() => {
    if (previousCode !== null) {
      onChange(previousCode);
      setPreviousCode(null);
    }
  }, [previousCode, onChange]);

  const handleAcceptChange = useCallback(() => {
    setPreviousCode(null);
  }, []);

  const hasGeneratedCode = previousCode !== null;

  return (
    <div className="flex flex-col w-full flex-1 max-h-full" onBlur={onBlur}>
      {label && !isExpanded && <NoxusFormLabel label={label} name={name} />}
      <div
        className="flex flex-col flex-1 text-xs leading-5 bg-background border rounded-md overflow-hidden relative max-h-full"
        style={{
          fontFamily: '"Fira Code", "Fira Mono", monospace',
        }}
      >
        {/* Toolbar */}
        {!isExpanded && (
          <div className="px-4 py-1 border-b justify-end flex">
            <NoxusIconButton
              icon={Maximize2}
              variant="tertiary"
              onClick={onOpen}
              isDisabled={isDisabled}
              aria-label={"Expand"}
            />
          </div>
        )}
        {/* Body */}
        <div
          className={cn(
            "p-4 overflow-auto relative flex-1",
            isExpanded ? "max-h-full" : "max-h-[25rem]"
          )}
          ref={containerRef}
        >
          <div
            className={cn("flex flex-1 h-min", isDisabled ? "opacity-50" : "")}
          >
            <div className="text-right min-w-8 p-0 pr-4 truncate text-primary h-min">
              <p className="whitespace-pre">{lineNumbers}</p>
            </div>
            {(value === "" || value === undefined) && (
              <p className="absolute top-4 left-12 text-muted-foreground whitespace-pre pointer-events-none">
                {placeholder}
              </p>
            )}
            <Editor
              value={value || ""}
              disabled={isDisabled}
              onValueChange={onChange}
              highlight={highlightCode}
              textareaClassName="code-editor"
              style={{
                minWidth: "max-content",
                minHeight: "6rem",
                flex: 1,
                whiteSpace: "pre-nowrap",
              }}
            />
          </div>
        </div>

        <CodeGenerateControls
          hasGenerated={hasGeneratedCode}
          boxVisible={isTextBoxVisible}
          disabled={isDisabled}
          onToggleBox={() => setIsTextBoxVisible(!isTextBoxVisible)}
          onAccept={handleAcceptChange}
          onRevert={handleRevertChange}
        />
      </div>

      <CodeGeneratePromptBox
        visible={isTextBoxVisible}
        value={inputValue}
        onChange={setInputValue}
        onSubmit={handleGenerateCode}
        isLoading={isLoading}
        placeholder="Describe the code you want to generate"
      />

      {helperText != undefined && (
        <div className="mt-1.5 text-xs font-regular text-muted-foreground">
          {helperText}
        </div>
      )}

      {!isExpanded && isOpen && (
        <ModalCodeEditor
          isOpen={isOpen}
          onClose={onClose}
          value={value || ""}
          placeholder={placeholder}
          onChange={onChange}
          isDisabled={isDisabled}
          label={label}
        />
      )}
    </div>
  );
};

export const CodeFieldAI = ({
  name,
  placeholder,
  label,
  isDisabled,
  inputVariablesFieldName,
  outputVariablesFieldName,
  filesAsObjectsFieldName,
  helperText,
}: ICodeFieldAI) => {
  const { watch } = useFormContext();

  const rawInputVariables = watch(inputVariablesFieldName);
  const rawOutputVariables = watch(outputVariablesFieldName);
  const filesAsObjects = filesAsObjectsFieldName
    ? !!watch(filesAsObjectsFieldName)
    : false;
  const inputVariables = useMemo(
    () => normalizeVariablesForCodegen(rawInputVariables),
    [rawInputVariables]
  );
  const outputVariables = useMemo(
    () => normalizeVariablesForCodegen(rawOutputVariables),
    [rawOutputVariables]
  );

  return (
    <Controller
      name={name}
      render={({ field }) => (
        <CodeInputAI
          label={label}
          value={field.value}
          placeholder={placeholder}
          onChange={field.onChange}
          onBlur={field.onBlur}
          isDisabled={isDisabled}
          name={name}
          helperText={helperText}
          inputVariables={inputVariables}
          outputVariables={outputVariables}
          filesAsObjects={filesAsObjects}
        />
      )}
    />
  );
};
